import React from 'react';
import {alpha, Box, Divider, Stack, styled} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import {
  CREDIT_ACCESS_ACTION,
  CreditAccessAction,
} from '@contentlab/utils/resolveCreditAccessAction';
import translate from '@/utils/translate';
import styles from './CreditEndModalContent.module.scss';

const featuresYouGet = [
  'More credits',
  'No watermark',
  'Extra team seats',
  'Basic support',
];

const IconWithBackground = styled(Icon, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<{
  bgColor?: string;
}>(({bgColor}) => ({
  backgroundColor: bgColor,
  borderRadius: '999px',
  padding: '4px',
}));

function CreditEndModalContent({
  status,
  onClose,
  onRedirectToPricing,
}: {
  status: CreditAccessAction;
  onClose: () => void;
  onRedirectToPricing: () => void;
}) {
  return (
    <Stack className={styles.container}>
      <Headline size="md" marginBottom="8px">
        {translate('Get More Credits.')}{' '}
        <span style={{color: designSystemToken('semantic.fg.brand')}}>
          {translate('Unlock More Videos.')}
        </span>
      </Headline>
      <Body size="md" marginBottom="16px">
        {status === CREDIT_ACCESS_ACTION.INSUFFICIENT_CREDITS
          ? translate('You need more credits to complete your project.')
          : translate('You’ve reached the credit limit of your current plan.')}
      </Body>
      <Stack direction="row" gap="12px">
        <IconWithBackground
          name="Features-Flash-Fill"
          size="medium"
          appearance="brand"
          bgColor={alpha(designSystemToken('semantic.bg.brand-secondary'), 0.3)}
        />
        <Stack marginBottom="16px">
          {status === CREDIT_ACCESS_ACTION.INSUFFICIENT_CREDITS ? (
            <Body size="md" marginBottom="16px">
              {translate('Upgrade your plan and get:')}
            </Body>
          ) : (
            <Headline size="xxs" marginBottom="16px">
              {translate('Upgrade your plan to complete your project and get:')}
            </Headline>
          )}
          {featuresYouGet.map((feature) => (
            <Stack direction="row" gap="8px" marginBottom="8px" key={feature}>
              <Icon name="Actions-Accept" size="small" appearance="primary" />
              <Body size="md">{translate(feature)}</Body>
            </Stack>
          ))}
        </Stack>
      </Stack>
      {status === CREDIT_ACCESS_ACTION.INSUFFICIENT_CREDITS && (
        <Divider sx={{marginBottom: '24px'}}>
          <Body size="lg" color={designSystemToken('semantic.fg.tertiary')}>
            {translate('or')}
          </Body>
        </Divider>
      )}
      {status === CREDIT_ACCESS_ACTION.INSUFFICIENT_CREDITS && (
        <Stack direction="row" gap="12px">
          <Box>
            <IconWithBackground
              name="Navigation-Edit"
              size="medium"
              appearance="primary"
              bgColor={designSystemToken('semantic.bg.tertiary')}
            />
          </Box>
          <Body size="md">
            {translate(
              'Adjust the project by uploading a shorter video or adding fewer output options to use fewer credits.'
            )}
          </Body>
        </Stack>
      )}
      <Button
        appearance="neutral"
        mode="filled"
        size="medium"
        sx={{marginTop: 'auto', marginBottom: '8px'}}
        onClick={onRedirectToPricing}
      >
        {translate('Contact Sales')}
      </Button>
      {status === CREDIT_ACCESS_ACTION.INSUFFICIENT_CREDITS && (
        <Button
          appearance="neutral"
          mode="plain"
          size="medium"
          onClick={onClose}
        >
          Back to Editor
        </Button>
      )}
    </Stack>
  );
}

export default CreditEndModalContent;
